import {
  configureStore,
  combineReducers,
  type Dispatch,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { persistStore, persistReducer, createMigrate } from 'redux-persist';
import localForage from 'localforage';
import type { PersistedState } from 'redux-persist/es/types';
import cartReducer from './cartSlice';
import draftReducer, { saveDraft } from './draftSlice';
import userSettingsReducer from './userSettingsSlice';
import authReducer from './authSlice';
import { debugWarn } from '../utils/debug';
import { isIOS } from '../utils/pwa-environment';
import type { DraftOrder } from './draftSlice';
import { DRAFT_PERSISTENCE_ERROR_EVENT } from '../consts/draft-persistence-events';
import type { CartItem } from './cartSlice';
import { isDraftPersistenceBlocked } from './draft-persistence-guard';

const ORDER_MODE_STORAGE_KEY = 'orderModeState';

interface OrderModePersisted {
  fromTours?: boolean;
  fromDraft?: boolean;
  promemoriaOpened?: boolean;
  customerCode: string;
  customerName?: string;
  date: string;
  draftId?: string | null;
  /** Modifica ordine già sul server: niente auto-bozza locale finché non si salva esplicitamente. */
  orderCode?: string;
  sourceOrderState?: string;
}

function readOrderModeState(): OrderModePersisted | null {
  try {
    const raw = localStorage.getItem(ORDER_MODE_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OrderModePersisted;
  } catch {
    return null;
  }
}

const cartPersistConfig = {
  key: 'cart',
  storage: localForage,
};

const draftsPersistConfig = {
  key: 'drafts',
  storage: localForage,
};

const userSettingsPersistConfig = {
  key: 'userSettings',
  storage: localForage,
};

const authMigrations = {
  1: (state: PersistedState): PersistedState => {
    const s = state as Record<string, unknown> | undefined;
    if (s && typeof s === 'object' && !('rememberMe' in s)) {
      return { ...s, rememberMe: false } as unknown as PersistedState;
    }
    return state;
  },
};

const authPersistConfig = {
  key: 'auth',
  storage: localForage,
  version: 1,
  migrate: createMigrate(authMigrations),
  // Non persistere stati transitori: evita bottone login bloccato dopo crash/reload
  blacklist: ['isLoading', 'error'],
};

const persistedCartReducer = persistReducer(cartPersistConfig, cartReducer);
const persistedDraftsReducer = persistReducer(
  draftsPersistConfig,
  draftReducer,
);
// Su iOS non persistere le impostazioni utente: memoria limitata, solo ordini e appuntamenti su disco.
const persistedUserSettingsReducer = (
  isIOS()
    ? userSettingsReducer
    : persistReducer(userSettingsPersistConfig, userSettingsReducer)
) as typeof userSettingsReducer;
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);

const rootReducer = combineReducers({
  cart: persistedCartReducer,
  drafts: persistedDraftsReducer,
  userSettings: persistedUserSettingsReducer,
  auth: persistedAuthReducer,
});

function dispatchDraftPersistenceError(message: string): void {
  window.dispatchEvent(
    new CustomEvent(DRAFT_PERSISTENCE_ERROR_EVENT, {
      detail: { message },
    }),
  );
}

const CART_ACTIONS_TO_PERSIST = new Set([
  'cart/addProduct',
  'cart/removeProduct',
  'cart/updateQuantity',
  'cart/updateProductNotes',
  'cart/setOrderNotes',
]);

function createDraftPersistenceMiddleware() {
  return (store: {
      getState: () => {
        drafts: { drafts: DraftOrder[] };
        auth: {
          selectedCompanyId: string | null;
          user: { sub: string } | null;
        };
        cart: { items: CartItem[]; orderNotes?: string };
      };
      dispatch: Dispatch<UnknownAction>;
    }) =>
    (next: (action: unknown) => unknown) =>
    (action: unknown) => {
      const a = action as { type?: string; payload?: unknown };
      const prevDraftsForSave =
        a?.type === 'drafts/saveDraft' ? store.getState().drafts.drafts : null;
      const prevIds =
        prevDraftsForSave != null
          ? new Set(prevDraftsForSave.map((d) => d.id))
          : null;

      const result = next(action);

      if (a?.type === 'drafts/saveDraft') {
        if (isDraftPersistenceBlocked()) {
          return result;
        }
        const payload = a.payload as Omit<DraftOrder, 'id'> & { id?: string };
        const stateAfter = store.getState();
        const companyId = stateAfter.auth.selectedCompanyId;
        const agentCode = stateAfter.auth.user?.sub ?? '';

        if (!companyId || !agentCode || !payload) {
          debugWarn(
            '[DraftPersistence] Skipped SQLite save: missing companyId, agentCode, or payload',
            {
              hasCompanyId: !!companyId,
              hasAgentCode: !!agentCode,
              hasPayload: !!payload,
            },
          );
          return result;
        }

        const nextDrafts = stateAfter.drafts.drafts;
        let resolved: DraftOrder | undefined;
        if (payload.id) {
          resolved = nextDrafts.find((d) => d.id === payload.id);
        } else if (prevIds) {
          resolved = nextDrafts.find((d) => !prevIds.has(d.id));
        }

        if (!resolved) {
          debugWarn(
            '[DraftPersistence] Could not resolve draft after saveDraft; skipping SQLite',
          );
          return result;
        }

        void import('../services/offline/local-order.service').then(
          ({ localOrderService }) => {
            if (isDraftPersistenceBlocked()) {
              return;
            }
            const latestDrafts = store.getState().drafts.drafts;
            const stillExists = latestDrafts.some((d) => d.id === resolved!.id);
            if (!stillExists) {
              return;
            }
            localOrderService.setContext(companyId, agentCode);
            void localOrderService.saveDraftOrder(resolved!).catch((err) => {
              debugWarn('[DraftPersistence] saveDraftOrder failed:', err);
              const msg =
                err instanceof Error ? err.message : String(err ?? '');
              dispatchDraftPersistenceError(msg);
            });
          },
        );
      } else if (a?.type === 'drafts/deleteDraft') {
        const draftId = a.payload as string;
        const stateAfter = store.getState();
        const companyId = stateAfter.auth.selectedCompanyId;
        const agentCode = stateAfter.auth.user?.sub ?? '';
        if (draftId) {
          if (companyId && agentCode) {
            void import('../services/offline/local-order.service').then(
              ({ localOrderService }) => {
                localOrderService.setContext(companyId, agentCode);
                void localOrderService
                  .deleteDraftOrder(draftId)
                  .catch((err) => {
                    debugWarn(
                      '[DraftPersistence] deleteDraftOrder failed:',
                      err,
                    );
                    const msg =
                      err instanceof Error ? err.message : String(err ?? '');
                    dispatchDraftPersistenceError(msg);
                  });
              },
            );
          } else {
            debugWarn(
              '[DraftPersistence] Skipped SQLite delete: missing companyId or agentCode',
              {
                hasCompanyId: !!companyId,
                hasAgentCode: !!agentCode,
              },
            );
          }
        }
      } else if (a?.type && CART_ACTIONS_TO_PERSIST.has(a.type)) {
        if (!isDraftPersistenceBlocked()) {
          persistCartToDraft(store.getState, store.dispatch);
        }
      }
      return result;
    };
}

function persistCartToDraft(
  getState: () => {
    auth: { selectedCompanyId: string | null; user: { sub: string } | null };
    cart: { items: CartItem[]; orderNotes?: string };
  },
  dispatch: Dispatch<UnknownAction>,
): void {
  const orderMode = readOrderModeState();
  if (!orderMode || !orderMode.customerCode || !orderMode.date) {
    return;
  }
  if (orderMode.orderCode) {
    return;
  }
  /** draftId solo da useOrderMode / navigazione — mai auto-creato qui (evita bozze fantasma in browse). */
  if (!orderMode.draftId) {
    return;
  }

  const state = getState();
  const companyId = state.auth.selectedCompanyId;
  const agentCode = state.auth.user?.sub ?? '';

  if (!companyId || !agentCode) {
    debugWarn(
      '[DraftPersistence] Skipped cart->Redux: missing companyId or agentCode',
    );
    return;
  }

  const draftId = orderMode.draftId;

  const draft: DraftOrder = {
    id: draftId,
    customer: {
      customerCode: orderMode.customerCode,
      customerName: orderMode.customerName ?? orderMode.customerCode,
    } as DraftOrder['customer'],
    orderDate: orderMode.date,
    items: state.cart.items,
    orderNotes: state.cart.orderNotes || undefined,
  };

  dispatch(saveDraft(draft));
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'persist/PURGE',
          'persist/FLUSH',
          'persist/PAUSE',
          'persist/REGISTER',
        ],
      },
    }).concat(createDraftPersistenceMiddleware()),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
